import { forwardRef, HTMLAttributes, ReactNode } from "react";
import { motion, HTMLMotionProps } from "motion/react";
import clsx from "clsx";

interface CardProps extends Omit<HTMLMotionProps<"div">, "children"> {
  variant?: "default" | "glass" | "elevated";
  padding?: "none" | "sm" | "md" | "lg";
  children?: ReactNode;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ variant = "default", padding = "md", className, children, ...props }, ref) => {
    const paddingClasses = {
      none: "",
      sm: "p-3",
      md: "p-4",
      lg: "p-6",
    };

    const variantClasses = {
      default: clsx(
        "bg-white dark:bg-[#111827]",
        "border border-gray-200/80 dark:border-lime-500/8",
        "rounded-2xl"
      ),
      glass: clsx(
        "bg-white/65 dark:bg-[#0d1420]/70",
        "backdrop-blur-2xl saturate-150",
        "border border-white/50 dark:border-lime-500/10",
        "rounded-2xl"
      ),
      elevated: clsx(
        "bg-white dark:bg-[#111827]",
        "border border-gray-200/80 dark:border-lime-500/8",
        "rounded-2xl",
        "shadow-lg shadow-lime-500/5 dark:shadow-[0_0_30px_-8px_rgba(132,204,22,0.1)]"
      ),
    };

    return (
      <motion.div
        ref={ref}
        className={clsx(
          variantClasses[variant],
          paddingClasses[padding],
          className
        )}
        {...props}
      >
        {children}
      </motion.div>
    );
  }
);

Card.displayName = "Card";

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          "pb-4 border-b border-gray-200/80 dark:border-lime-500/8",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardHeader.displayName = "CardHeader";

interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={clsx("py-4", className)} {...props}>
        {children}
      </div>
    );
  }
);

CardBody.displayName = "CardBody";

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          "pt-4 border-t border-gray-200/80 dark:border-lime-500/8",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = "CardFooter";

export default Card;
export { CardHeader, CardBody, CardFooter };
